import { useEffect, useState } from 'react';
import { Button, Card, Field, Input, ShareIcon } from '@viniciomendesr/rayrovisk-design-system';
import { isNetworkHandle, networkHandle } from '../api/handle';
import type { Network, SocialAccount } from '../api/types';
import { useSaveSocialAccounts } from '../api/queries';
import { NETWORK_INVALID, NETWORK_LABEL, NETWORK_PLACEHOLDER } from './mediaKitCopy';

const NETWORKS: Network[] = ['instagram', 'tiktok', 'youtube'];

type Draft = Record<Network, string>;

function toDraft(accounts: SocialAccount[]): Draft {
  return {
    instagram: accounts.find((a) => a.network === 'instagram')?.handle ?? '',
    tiktok: accounts.find((a) => a.network === 'tiktok')?.handle ?? '',
    youtube: accounts.find((a) => a.network === 'youtube')?.handle ?? ''
  };
}

/**
 * The first invalid field, as a sentence for the disabled button — the pattern
 * the rest of this app uses instead of inline field errors.
 */
function invalidOn(draft: Draft): string | undefined {
  for (const network of NETWORKS) {
    const typed = draft[network].trim();
    if (typed !== '' && !isNetworkHandle(network, typed)) {
      return NETWORK_INVALID[network];
    }
  }
  return undefined;
}

export function SocialLinksCard({
  accounts,
  onNetworkAdded
}: {
  accounts: SocialAccount[];
  /**
   * A network that was not here a moment ago has no numbers under it, and the
   * card would sit at "—" with nothing telling her what to do next. The read is
   * started by the screen, which owns the waiting.
   */
  onNetworkAdded?: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(accounts));
  const [saved, setSaved] = useState('');
  const save = useSaveSocialAccounts();

  useEffect(() => {
    setDraft(toDraft(accounts));
  }, [accounts]);

  const invalid = invalidOn(draft);
  const dirty = NETWORKS.some((network) => draft[network].trim() !== toDraft(accounts)[network]);

  async function submit() {
    // Normalized here as well as on the server: what comes back is what the
    // field redraws, and sending the pasted URL would make it flicker from the
    // URL to the handle after the round trip.
    const wanted = NETWORKS.flatMap((network) => {
      const handle = networkHandle(network, draft[network]);
      return handle ? [{ network, handle }] : [];
    });
    const before = new Set(accounts.map((account) => account.network));
    const added = wanted.some((entry) => !before.has(entry.network));

    await save.mutateAsync(wanted);
    setSaved(added ? 'Rede adicionada. Estamos lendo os números dela.' : 'Redes salvas.');
    if (added) {
      onNetworkAdded?.();
    }
  }

  return (
    <Card layer="functional" padding="lg">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h2
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              font: '600 17px/1.3 var(--rvk-font-expressive)',
              margin: 0
            }}
          >
            <ShareIcon size={18} aria-hidden />
            Minhas redes
          </h2>
          <p
            style={{
              font: '400 13px/1.5 var(--rvk-font-functional)',
              color: 'var(--rvk-text-secondary)',
              margin: 0
            }}
          >
            Deixe em branco a rede que você não usa. Nenhuma senha é pedida em nenhum momento.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {NETWORKS.map((network) => (
            <Field key={network} label={NETWORK_LABEL[network]}>
              <Input
                value={draft[network]}
                placeholder={NETWORK_PLACEHOLDER[network]}
                inputMode="url"
                autoComplete="off"
                onChange={(event) => {
                  setSaved('');
                  setDraft((current) => ({ ...current, [network]: event.target.value }));
                }}
              />
            </Field>
          ))}
        </div>

        {save.isError && (
          <p
            role="alert"
            style={{
              font: '400 13px/1.5 var(--rvk-font-functional)',
              color: 'var(--rvk-status-error)',
              margin: 0
            }}
          >
            {(save.error as Error).message}
          </p>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap'
          }}
        >
          {saved !== '' && !save.isPending && (
            <span
              role="status"
              style={{
                font: '400 13px/1.5 var(--rvk-font-functional)',
                color: 'var(--rvk-text-secondary)'
              }}
            >
              {saved}
            </span>
          )}
          <Button
            onClick={submit}
            loading={save.isPending}
            access={invalid || !dirty ? 'unavailable' : undefined}
            accessReason={invalid ?? 'Nada mudou por aqui.'}
          >
            Salvar redes
          </Button>
        </div>
      </div>
    </Card>
  );
}
